import type { AppData } from '../../data/types';
import type { SiteModel } from '../../site/types';
import type { Vec2 } from '../../geo/types';
import { createRng } from '../../core/rng';
import { distancePointSegment, pointInRing, ringBounds } from '../../geo/polygon';
import { createSpatialHash } from '../../geo/spatialHash';
import type { PlannedLots } from '../fabric/fabricLots';
import { collectLifeEntrances } from './entrances';
import { buildStreetGraph, distance } from './streetGraph';
import {
  createLifeClearance,
  lifeObstacleRings,
  openSquares,
  planLifeRoutes,
  routeClear,
} from './routes';
import { precinctRoutines } from './precinctRoutines';
import { pedestrianPath } from './pedestrianPath';
import { nightRoutines } from './nightRoutines';
import { doorTravel, heading, move, stay, travel, type Access } from './itinerary';
import type { CitizenRole, LifePlan, LifeProp, LifeRoute, LifeStep } from './types';
import { planCrowd } from './crowd/crowdPlan';
import { gasthuisRoutines } from './gasthuisRoutines';
import { physicianRoutines } from './physicianRoutines';

const CAST: readonly [CitizenRole, number][] = [
  ['trader', 24],
  ['baker', 8],
  ['butcher', 6],
  ['craftsman', 10],
  ['sailor', 10],
  ['shipmaster', 2],
  ['gentleperson', 4],
  ['burgomaster', 1],
];

/** All expensive connectivity and opening queries are baked; the browser reads finite itineraries. */
export function planNpcLife(
  data: AppData,
  site: SiteModel,
  lots: PlannedLots,
  seed: number
): LifePlan {
  const obstacles = lifeObstacleRings(data.layout, lots, site);
  const clearance = createLifeClearance(site, obstacles, openSquares(data.layout));
  const graph = buildStreetGraph(data.layout, clearance);
  const { entrances, windows, doors } = collectLifeEntrances(data, lots, seed);
  const hash = createSpatialHash<(typeof obstacles)[number]>(16);
  for (const ring of obstacles) hash.insert(ring, ringBounds(ring));
  const access: Access[] = [];
  for (const entry of entrances) {
    const approach = move(entry.position, entry.outward, 2.1);
    const node = graph.nearest(approach, 10);
    if (
      node === null ||
      !routeClear({ a: approach, b: graph.nodes[node]!.position, radius: 0.48 }, clearance)
    )
      continue;
    const owner = lots.houses.find(
      (lot) => `door:${lot.kind}:${lot.sourceId}:${lot.side}:${lot.ordinal}` === entry.id
    );
    if (!owner) continue;
    const local = hash.query({
      minX: entry.position[0] - 3,
      maxX: entry.position[0] + 3,
      minY: entry.position[1] - 3,
      maxY: entry.position[1] + 3,
    });
    // The terminal corridor may cross its own step and threshold, never another house or water.
    const forbidden = local.filter(
      (ring) =>
        !pointInRing(move(entry.position, entry.outward, -0.15), ring) &&
        !pointInRing(entry.position, ring)
    );
    let clear = true;
    for (let t = -0.35; t <= 2.1; t += 0.2) {
      const p = move(entry.position, entry.outward, t);
      if (
        site.waterIndex.contains(p) ||
        forbidden.some(
          (ring) =>
            pointInRing(p, ring) ||
            ring.some(
              (a, i) => distancePointSegment(p, { a, b: ring[(i + 1) % ring.length]! }) < 0.5
            )
        )
      ) {
        clear = false;
        break;
      }
    }
    if (clear) access.push({ entry, approach, node });
  }
  const rng = createRng(seed).fork('daily-life'),
    routes: LifeRoute[] = [],
    used = new Set<string>();
  const homes = access.filter((a) => a.entry.kind === 'home');
  const shops = access.filter((a) => a.entry.kind !== 'home');
  for (const [role, count] of CAST) {
    let found = 0;
    const preferred = [...homes].sort(
      (a, b) => anchorDistance(role, a, data) - anchorDistance(role, b, data)
    );
    for (const home of preferred) {
      if (found >= count) break;
      if (used.has(home.entry.id)) continue;
      const targets = shops
        .filter((shop) => {
          const d = distance(home.approach, shop.approach);
          return d > 35 && d < 180;
        })
        .sort(
          (a, b) =>
            (role === 'baker'
              ? Number(b.entry.kind === 'bakery') - Number(a.entry.kind === 'bakery')
              : 0) || distance(home.approach, a.approach) - distance(home.approach, b.approach)
        );
      for (const shop of targets) {
        const path = graph.path(home.node, shop.node);
        if (!path) continue;
        const points = [home.approach, ...path, shop.approach];
        const length = points.slice(1).reduce((sum, p, i) => sum + distance(points[i]!, p), 0);
        if (length < 40 || length > 350) continue;
        const speed = role === 'burgomaster' ? 0.85 : rng.range(0.9, 1.18);
        const prop: LifeProp =
          role === 'craftsman'
            ? 'tool'
            : role === 'sailor'
              ? 'plank'
              : role === 'baker' || role === 'butcher'
                ? 'basket'
                : 'parcel';
        const steps: LifeStep[] = [];
        const lane = rng.range(0.55, 1.65);
        stay(steps, home, rng.range(35, 120), 'inside');
        doorTravel(steps, home, true, speed);
        travel(steps, pedestrianPath(points, clearance, lane), speed);
        stay(
          steps,
          shop,
          rng.range(8, 18),
          role === 'craftsman' || role === 'baker' || role === 'butcher' ? 'work' : 'wait'
        );
        doorTravel(steps, shop, false, speed);
        stay(steps, shop, rng.range(20, 65), 'inside');
        doorTravel(steps, shop, true, speed, prop);
        travel(steps, pedestrianPath([...points].reverse(), clearance, lane), speed, prop);
        doorTravel(steps, home, false, speed, prop);
        const duration = steps.reduce((sum, step) => sum + step.seconds, 0);
        const woman =
          ['trader', 'baker', 'butcher', 'gentleperson'].includes(role) && found % 3 === 1;
        routes.push({
          id: `${role}-${routes.length}`,
          role,
          ...(woman ? { appearance: 'woman' as const } : {}),
          streetId: home.entry.streetId,
          a: home.approach,
          b: shop.approach,
          radius: 0.48,
          speed,
          scale: rng.range(0.94, 1.025),
          phase: rng.range(0, duration),
          routine: steps,
          daytime: true,
        });
        used.add(home.entry.id);
        used.add(shop.entry.id);
        found++;
        break;
      }
    }
  }
  // Dogs follow a real pedestrian itinerary and pause with their owner. No random darting into water.
  for (const owner of routes.filter((route) => route.role === 'trader').slice(0, 6)) {
    const animalRoute = { ...owner };
    delete animalRoute.appearance;
    routes.push({
      ...animalRoute,
      id: `dog-${routes.length}`,
      role: 'dog',
      scale: 0.7,
      radius: 0.48,
      phase: owner.phase - 1.1,
      routine: owner.routine!.map(({ prop: _prop, ...step }) => step),
    });
  }
  routes.push(...precinctRoutines(data.layout, site, obstacles));
  routes.push(...gasthuisRoutines(data.layout, clearance));
  routes.push(
    ...physicianRoutines(data.layout, graph, homes, used, clearance, rng.fork('physician'))
  );
  const horse = planLifeRoutes(data.layout, clearance).find((route) => route.role === 'horse');
  if (horse) {
    const dir: Vec2 = [
      (horse.b[0] - horse.a[0]) / distance(horse.a, horse.b),
      (horse.b[1] - horse.a[1]) / distance(horse.a, horse.b),
    ];
    let a = horse.a,
      b = horse.b;
    for (let d = 1; d <= 45; d++) {
      const next = move(horse.b, dir, d);
      if (!routeClear({ a, b: next, radius: horse.radius }, clearance)) break;
      b = next;
    }
    for (let d = 1; d <= 45; d++) {
      const next = move(horse.a, dir, -d);
      if (!routeClear({ a: next, b, radius: horse.radius }, clearance)) break;
      a = next;
    }
    const steps: LifeStep[] = [];
    travel(steps, [a, b], horse.speed);
    steps.push({ from: b, to: b, seconds: 20, action: 'wait', heading: heading(b, a) });
    travel(steps, [b, a], horse.speed);
    steps.push({ from: a, to: a, seconds: 25, action: 'wait', heading: heading(a, b) });
    if (distance(a, b) >= 35) routes.push({ ...horse, a, b, routine: steps, daytime: true });
  }
  routes.push(...nightRoutines(data.layout, graph, homes, used, clearance, rng.fork('night')));
  const usedDoors = new Set(
    routes.flatMap(
      (route) => route.routine?.flatMap((step) => (step.door ? [step.door] : [])) ?? []
    )
  );
  const nearRoutes = createSpatialHash<number>(16);
  routes.forEach((route, i) => {
    for (const step of route.routine ?? []) {
      if (step.action === 'inside') continue;
      nearRoutes.insert(i, {
        minX: Math.min(step.from[0], step.to[0]) - 4,
        maxX: Math.max(step.from[0], step.to[0]) + 4,
        minY: Math.min(step.from[1], step.to[1]) - 4,
        maxY: Math.max(step.from[1], step.to[1]) + 4,
      });
    }
  });
  const clearanceRings = obstacles.filter((ring) => nearRoutes.query(ringBounds(ring)).length > 0);
  return {
    routes,
    entrances: access.map((a) => a.entry).filter((e) => usedDoors.has(e.id)),
    windows,
    crowd: planCrowd(data.layout, site, lots, doors),
    clearanceRings,
  };
}

function anchorDistance(role: CitizenRole, access: Access, data: AppData): number {
  const id =
    role === 'friar'
      ? 'landmark-minderbroeders'
      : role === 'sister'
        ? 'landmark-sint-lucien'
        : role === 'sailor' || role === 'shipmaster'
          ? 'landmark-nieuwe-brug-east-headhouse'
          : 'landmark-oude-stadhuis';
  const anchor = data.layout.landmarks.find((l) => l.id === id)?.footprint[0] ?? [0, 0];
  return distance(access.approach, anchor);
}
